import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MenuItem } from '../models/menu.model';
import { FulfillmentMode } from '../models/order.model';
import { AddCartItem, Cart, CartItem } from '../models/cart.model';
import { Branch } from '../models/brand.model';
import { CartService, PromoChangeDeclined } from './cart.service';
import { SessionService, ActiveBranch } from './session.service';
import { NavService } from './nav.service';
import { BranchService } from './branch.service';
import { I18nService } from './i18n.service';
import { ItemService, detailToMenuItem } from './item.service';
import { PREVIEW } from '../preview/preview-mode';

export interface LoginGate { open: boolean; reason: string; }

/**
 * UI-level state that lives above the routed screens: transient toast,
 * bottom-sheet overlays (item options, login, fulfillment), and the
 * login-gated add-to-cart flow.
 */
@Injectable({ providedIn: 'root' })
export class UiService {
  private readonly _toast = new BehaviorSubject<{ text: string; error: boolean } | null>(null);
  private readonly _itemSheet = new BehaviorSubject<MenuItem | null>(null);
  private readonly _itemLoading = new BehaviorSubject<boolean>(false);
  private readonly _login = new BehaviorSubject<LoginGate>({ open: false, reason: '' });
  private readonly _fulfillOpen = new BehaviorSubject<boolean>(false);
  private readonly _searchOpen = new BehaviorSubject<boolean>(false);

  readonly toast$ = this._toast.asObservable();
  readonly itemSheet$ = this._itemSheet.asObservable();
  readonly itemLoading$ = this._itemLoading.asObservable();
  readonly login$ = this._login.asObservable();
  readonly fulfillOpen$ = this._fulfillOpen.asObservable();
  readonly searchOpen$ = this._searchOpen.asObservable();

  private pendingAdd: { req: AddCartItem; name: string } | null = null;
  private pendingDineIn = false;
  private toastTimer: any = null;

  /** Open the table scanner; if not signed in, log in first then continue to it. */
  startDineIn(): void {
    if (this.blockedInPreview()) return;
    if (this.session.loggedIn) { this.nav.nav('dinein'); return; }
    this.pendingDineIn = true;
    this.requireLogin('Log in to start a table order');
  }

  constructor(
    private cart: CartService,
    private session: SessionService,
    private nav: NavService,
    private branchSvc: BranchService,
    private i18n: I18nService,
    private itemSvc: ItemService,
  ) {}

  // ── admin live preview ──
  /**
   * The admin live preview is read-only (Home and Menu only): nothing that
   * signs in, picks an order type or touches the cart may start there. True —
   * after telling the viewer why — when the caller must stop.
   */
  private blockedInPreview(): boolean {
    if (!PREVIEW.active) return false;
    this.closeItem();
    this.showToast('Preview only — ordering is turned off here.');
    return true;
  }

  // ── toast ──
  showToast(msg: string, error = false): void {
    this._toast.next({ text: msg, error });
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this._toast.next(null), error ? 2600 : 1700);
  }

  // ── item options sheet ──
  /** Open the sheet in a loading state while item details are fetched. */
  beginItemLoad(): void { this._itemSheet.next(null); this._itemLoading.next(true); }
  openItem(item: MenuItem, editing: CartItem | null = null): void {
    this.editingLine = editing;
    this._itemSheet.next(item);
    this._itemLoading.next(false);
  }
  closeItem(): void { this.editingLine = null; this._itemLoading.next(false); this._itemSheet.next(null); }

  /** The cart line the item sheet is editing (null → the sheet adds a new line). */
  editingLine: CartItem | null = null;

  /** Reopen a cart line in the item sheet, prefilled with its current configuration. */
  editCartLine(line: CartItem, fallbackImage = ''): void {
    if (this.blockedInPreview()) return;
    const branchId = this.cart.cart.branchId || this.branchSvc.currentId;
    if (!branchId) return;
    this.beginItemLoad();
    this.itemSvc.getItem(line.menuItemId, branchId).subscribe({
      next: (detail) => this.openItem(detailToMenuItem(detail, line.imageUrl || fallbackImage), line),
      error: () => { this.closeItem(); this.showToast('Could not load this item'); },
    });
  }

  /** Save the item sheet's reconfigured line over the one being edited. */
  updateCartLine(req: AddCartItem, name: string): void {
    const line = this.editingLine;
    if (!line) return;
    this.cart.replaceLine(line.id, this.withCartContext(req)).subscribe({
      next: () => { this.showToast(`${this.i18n.t('Updated')} · ${name}`); this.closeItem(); },
      error: (e: any) => {
        this.showToast(e instanceof PromoChangeDeclined
          ? 'Item not changed — your offer is still applied.'
          : (e?.error?.message || 'Couldn’t update this item'), !(e instanceof PromoChangeDeclined));
        this.closeItem();
      },
    });
  }

  // ── desktop search palette ──
  openSearch(): void { this._searchOpen.next(true); }
  closeSearch(): void { this._searchOpen.next(false); }

  // ── fulfillment sheet ──
  /** Mode the opener asked the sheet to land on; consumed by the next open. */
  private wantFulfillMode: FulfillmentMode | null = null;

  /**
   * Open the order-type sheet. `mode` preselects a tab for openers that already
   * name one — the "Scan to dine-in" button must not land on Pickup.
   */
  openFulfillment(mode?: FulfillmentMode): void {
    if (this.blockedInPreview()) return;
    if (mode) this.wantFulfillMode = mode;
    this._fulfillOpen.next(true);
  }
  /** The requested mode, read once by the sheet as it opens. */
  takeFulfillmentMode(): FulfillmentMode | null {
    const m = this.wantFulfillMode;
    this.wantFulfillMode = null;
    return m;
  }
  closeFulfillment(): void { this.wantFulfillMode = null; this._fulfillOpen.next(false); }

  // ── login gate ──
  requireLogin(reason = ''): void {
    if (this.blockedInPreview()) return;
    this._login.next({ open: true, reason });
  }
  closeLogin(): void { this._login.next({ open: false, reason: '' }); }

  /**
   * Finalize a successful sign-in. Auth is already stored on the session by the
   * login sheet. A newly-registered user who hasn't picked an order type yet is
   * sent straight into the order-type sheet (per the register→setup flow);
   * otherwise any pending cart line is committed.
   */
  completeLogin(): void {
    this.closeLogin();
    // Load the account's server cart now that we're authenticated.
    this.cart.load(this.session.branch.id || null);
    // Ask for the device location (nearest branch + distances), then open the picker.
    this.requestLocation();
    // Signed in to scan a table → continue straight to the QR scanner.
    if (this.pendingDineIn) { this.pendingDineIn = false; this.nav.nav('dinein'); return; }
    // Mid add-to-cart → resume it (its gates handle order-type setup); otherwise
    // always open the order-type picker so the customer chooses after signing in.
    if (this.pendingAdd) { this.tryCommitPending(); return; }
    this.openFulfillment();
  }

  /** Prompt for location; on grant, refresh branches so nearest/distances resolve. */
  private requestLocation(): void {
    // Already have coords (e.g. the register→location step just captured them) —
    // don't fire a second browser prompt; just refresh branches with the headers.
    if (this.session.coords) { this.branchSvc.load(); return; }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.session.setCoords(pos.coords.latitude, pos.coords.longitude);
        this.branchSvc.load(); // re-fetch branches with the location headers (nearest + distances)
      },
      () => { /* denied / unavailable — continue without distances */ },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  /** Dine-in: the table QR was scanned — record it and resume any pending add. */
  confirmTableScan(): void {
    this.session.setTableScanned(true);
    this.showToast('Table scanned');
    this.tryCommitPending();
  }

  /** Whether a configured line is queued behind a gate (used by the scanner to auto-continue). */
  get hasPendingAdd(): boolean { return !!this.pendingAdd; }

  /** Resume a queued add once the table QR is bound. Returns whether one was pending. */
  resumePendingAdd(): boolean {
    const had = !!this.pendingAdd;
    this.tryCommitPending();
    return had;
  }

  /**
   * Apply an order-type + branch/address selection: empties the cart when the
   * branch changes, marks setup complete, closes the sheet, and resumes any
   * add that was waiting behind the setup gate. Returns whether the branch changed.
   */
  applyFulfillment(mode: FulfillmentMode, branch: Branch | ActiveBranch, addressId: string | null = null): boolean {
    const branchChanged = this.session.configured && this.session.branch.id !== branch.id;
    // Snapshot the selection so we can put it back if the server says the change
    // would void the applied offer and the customer chooses to keep the offer.
    const prev = {
      mode: this.session.fulfillment,
      branch: this.session.branch,
      addressId: this.session.addressId,
      tableScanned: this.session.dineTableScanned,
      menuBranchId: this.branchSvc.currentId,
    };
    this.session.setFulfillment(mode);
    this.session.setBranch(branch);
    this.session.setAddressId(mode === 'delivery' ? addressId : null);
    // A new order-type / branch selection invalidates any previous table scan.
    this.session.setTableScanned(false);
    this.session.markConfigured();
    this.closeFulfillment();
    // Sync the server cart context (order type / branch / address) — the server
    // keeps lines across a branch change and re-resolves availability — then
    // resume any add that was waiting behind the setup gate.
    if (this.session.loggedIn) {
      this.cart.setContextConfirmingPromoDrop({
        orderType: toApiOrderType(mode),
        branchId: branch.id || null,
        addressId: mode === 'delivery' ? addressId : null,
      }).subscribe({
        next: () => this.tryCommitPending(),
        error: (e) => {
          // Offer kept → undo the selection so the screen matches the server cart.
          if (e instanceof PromoChangeDeclined) { this.revertFulfillment(prev); return; }
          this.tryCommitPending();
        },
      });
    } else {
      this.tryCommitPending();
    }
    return branchChanged;
  }

  /** Put back the order-type selection captured before a rejected context change. */
  private revertFulfillment(prev: {
    mode: FulfillmentMode; branch: ActiveBranch; addressId: string | null;
    tableScanned: boolean; menuBranchId: string | null;
  }): void {
    this.session.setFulfillment(prev.mode);
    this.session.setBranch(prev.branch);
    this.session.setAddressId(prev.addressId);
    this.session.setTableScanned(prev.tableScanned);
    this.branchSvc.setCurrent(prev.menuBranchId);
    this.pendingAdd = null; // the gate it was queued behind never opened
    this.showToast('Order type unchanged — your offer is still applied.');
  }

  // ── gated add-to-cart ──
  /**
   * Queue a configured line and run the ordered gates. The add is held until
   * every gate passes; each gate's resolver (sign-in, order-type setup, table
   * scan) re-runs this check so the add resumes automatically.
   */
  addConfiguredItem(req: AddCartItem, name: string, unitPrice = 0, variantName = '', optionLabels: string[] = []): void {
    if (this.blockedInPreview()) return;
    // Capture mode (e.g. the reservation builder) intercepts the configured line
    // instead of committing it to the order cart.
    if (this.captureAdd) { this.captureAdd(req, name, unitPrice, variantName, optionLabels); this.closeItem(); return; }
    this.pendingAdd = { req, name };
    this.tryCommitPending();
  }

  /** Redirect configured item-sheet output to a handler (reservation cart) instead of the order cart. */
  private captureAdd: ((req: AddCartItem, name: string, unitPrice: number, variantName: string, optionLabels: string[]) => void) | null = null;
  setItemCapture(fn: (req: AddCartItem, name: string, unitPrice: number, variantName: string, optionLabels: string[]) => void): void { this.captureAdd = fn; }
  clearItemCapture(): void { this.captureAdd = null; }

  /**
   * Ordered gates: (1) signed in → (2) order type + delivery address →
   * (3) dine-in table scanned. Posts the line to the server cart once all pass.
   */
  private tryCommitPending(): void {
    const pending = this.pendingAdd;
    if (!pending) return;

    // 1) not logged in → phone login dialog
    if (!this.session.loggedIn) {
      this.closeItem();
      this.requireLogin('Sign in to start your order. Your cart is saved on this device.');
      return;
    }
    // 2) no order type, or delivery without an address → order-type sheet
    if (!this.session.configured || (this.session.fulfillment === 'delivery' && !this.session.addressId)) {
      this.closeItem();
      this.showToast('Kindly select order type / location first.');
      this.openFulfillment();
      return;
    }
    // 3) dine-in without a bound table → open the QR scanner (the add resumes after binding)
    if (this.session.fulfillment === 'dinein' && !this.session.dineTableScanned) {
      this.closeItem();
      this.showToast('Scan your table QR to continue.');
      this.nav.nav('dinein');
      return;
    }

    // all gates passed → post the line as its own cart line
    this.pendingAdd = null;
    const before = new Map(this.cart.cart.items.map((i) => [i.id, i.quantity]));
    this.cart.addItem(this.withCartContext(pending.req)).subscribe({
      next: (c) => {
        // A 2xx doesn't mean the item went in: the server skips a sold-out item, or adds it as an
        // unavailable (uncharged) line. Only claim success when a sellable line actually landed.
        const line = addedLine(before, c, pending.req);
        if (!line || !line.available) {
          if (line && !before.has(line.id)) this.cart.discardLine(line.id);
          this.showToast(`${pending.name} · ${this.i18n.t(line?.unavailableReason || 'Sold out')} — ${this.i18n.t('not added to your cart')}`, true);
          return; // keep the sheet open so another size / option can be picked
        }
        // The item name is data, so only the prefix is translated here — the toast
        // pipe can't match a string that already carries an interpolated name.
        this.showToast(`${this.i18n.t('Added')} · ${pending.name}`); this.closeItem();
      },
      error: (e: any) => {
        // Offer kept → the line was never posted; that's a choice, not a failure.
        this.showToast(e instanceof PromoChangeDeclined
          ? 'Item not added — your offer is still applied.'
          : (e?.error?.message || 'Couldn’t add to cart'));
        this.closeItem();
      },
    });
  }
  /** Stamp the session's branch / order type / delivery address onto a line request. */
  private withCartContext(req: AddCartItem): AddCartItem {
    return {
      ...req,
      branchId: req.branchId || this.session.branch.id || null,
      orderType: req.orderType || toApiOrderType(this.session.fulfillment),
      // Delivery orders carry the chosen delivery address.
      addressId: this.session.fulfillment === 'delivery' ? (req.addressId || this.session.addressId || null) : null,
    };
  }
}

/** The line an add created or grew (same item + size), or null when the server didn't add it. */
function addedLine(before: Map<string, number>, after: Cart, req: AddCartItem): CartItem | null {
  return after.items.find((i) => i.menuItemId === req.menuItemId
    && (!req.variantId || i.variantId === req.variantId)
    && (!before.has(i.id) || i.quantity > (before.get(i.id) ?? 0))) || null;
}

/** Map the app's fulfillment mode onto the API's order_type. */
function toApiOrderType(m: FulfillmentMode): string {
  return m === 'pickup' ? 'pickup' : m === 'dinein' ? 'dine_in' : 'delivery';
}
